/**
 * krv/AbrechnungSektion – Leistungsabrechnungen der Versicherung (Welle 95).
 *
 * Eine Abrechnung ist EINE Datei, deckt aber MEHRERE eingereichte Belege ab.
 * Aufklappen zeigt die Positionszeilen; je Position schlägt das Programm einen
 * Beleg vor (Betrag, Datum, Behandler, Person). Zugeordnet wird per Klick —
 * die Entscheidung trifft immer der Mensch, nie die Automatik.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FaFilePdf } from "react-icons/fa";
import { S, api, eur, datum, IconKnopf } from "./gemeinsam";

function Positionen({ abrechnung, alleBelege, onGeaendert }) {
  const [zeilen, setZeilen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [wahl, setWahl] = useState({});          // position_id → beleg_id

  const laden = useCallback(async () => {
    setFehler("");
    try {
      const r = await api(`/abrechnungen/${abrechnung.id}/positionen`);
      setZeilen(r.positionen || []);
    } catch (e) { setFehler(String(e.message || e)); }
  }, [abrechnung.id]);

  React.useEffect(() => { laden(); }, [laden]);

  const auswerten = async () => {
    setBusy(true); setFehler("");
    try { await api(`/abrechnungen/${abrechnung.id}/auswerten?erzwinge=1`, "POST"); await laden(); onGeaendert(); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const neuRechnen = async () => {
    setBusy(true); setFehler("");
    try { await api(`/abrechnungen/${abrechnung.id}/vorschlaege`, "POST"); await laden(); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const bestaetigen = async (p) => {
    const belegId = wahl[p.id] || p.vorschlag_beleg_id;
    if (!belegId) return;
    setBusy(true); setFehler("");
    try {
      await api(`/abrechnungen/positionen/${p.id}/bestaetigen`, "POST", { beleg_id: Number(belegId) });
      await laden(); onGeaendert();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const setzeStatus = async (p, was) => {
    setBusy(true); setFehler("");
    try { await api(`/abrechnungen/positionen/${p.id}/${was}`, "POST"); await laden(); onGeaendert(); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  if (zeilen === null) return <div style={{ padding: "8px 16px", fontSize: "0.85rem" }}>Lade Positionen…</div>;

  return (
    <div style={{ padding: "8px 16px 14px", background: "var(--color-gray-100)" }}>
      {fehler && <div style={S.err}>{fehler}</div>}
      {zeilen.length === 0 ? (
        <div style={{ fontSize: "0.85rem", color: "var(--color-gray-600)", marginBottom: 8 }}>
          Diese Abrechnung ist noch nicht ausgewertet.
        </div>
      ) : (
        <table style={S.tabelle}>
          <thead><tr>
            <th style={S.th}>Person</th><th style={S.th}>Behandler</th><th style={S.th}>Belegdatum</th>
            <th style={{ ...S.th, textAlign: "right" }}>Betrag</th>
            <th style={{ ...S.th, textAlign: "right" }}>Erstattung</th>
            <th style={S.th}>Zugeordneter Beleg</th><th style={S.th}></th>
          </tr></thead>
          <tbody>
            {zeilen.map((p) => {
              const fertig = p.status === "bestaetigt";
              const verworfen = p.status === "verworfen";
              return (
                <tr key={p.id} style={verworfen ? { opacity: 0.55 } : null}>
                  <td style={S.td}>{p.person || "—"}</td>
                  <td style={S.td}>
                    {p.arzt || "—"}
                    {p.hinweis && (
                      <div style={{ fontSize: "0.72rem", color: "var(--color-gray-600)" }}>{p.hinweis}</div>
                    )}
                  </td>
                  <td style={S.td}>{datum(p.datum_beleg)}</td>
                  <td style={{ ...S.td, ...S.num }}>{eur(p.betrag)}</td>
                  <td style={{ ...S.td, ...S.num }}>{eur(p.erstattung)}</td>
                  <td style={S.td}>
                    {fertig ? (
                      <span style={{ fontWeight: 600 }}>✓ {p.beleg_datei}</span>
                    ) : verworfen ? (
                      <span style={{ color: "var(--color-gray-600)" }}>verworfen</span>
                    ) : (
                      <>
                        <select style={{ ...S.input, width: "100%", maxWidth: 380 }}
                                value={wahl[p.id] ?? (p.vorschlag_beleg_id || "")}
                                onChange={(e) => setWahl((w) => ({ ...w, [p.id]: e.target.value }))}>
                          <option value="">— kein Beleg gewählt —</option>
                          {alleBelege.map((b) => (
                            <option key={b.id} value={b.id}>
                              {datum(b.datum_beleg)} · {eur(b.betrag)} · {b.arzt || b.datei_name}
                            </option>
                          ))}
                        </select>
                        {p.vorschlag_beleg_id && (
                          <div style={{ fontSize: "0.72rem", color: "var(--color-gray-600)", marginTop: 2 }}>
                            Vorschlag: {p.vorschlag_grund} ({Math.round(p.vorschlag_score)} Punkte)
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                    {fertig || verworfen ? (
                      <button style={S.btnMini} onClick={() => setzeStatus(p, "oeffnen")} disabled={busy}>
                        zurücknehmen
                      </button>
                    ) : (
                      <>
                        <button style={S.btnPrim} onClick={() => bestaetigen(p)}
                                disabled={busy || !(wahl[p.id] || p.vorschlag_beleg_id)}>
                          zuordnen
                        </button>
                        <button style={S.btnMini} onClick={() => setzeStatus(p, "verwerfen")} disabled={busy}>
                          verwerfen
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, fontSize: "0.78rem",
                    color: "var(--color-gray-600)" }}>
        <span style={{ flex: 1, wordBreak: "break-all" }}>{abrechnung.datei_pfad}</span>
        <button style={S.btn} onClick={neuRechnen} disabled={busy}>Vorschläge neu rechnen</button>
        <button style={S.btn} onClick={auswerten} disabled={busy}>
          {busy ? "Werte aus…" : zeilen.length ? "Erneut auswerten" : "Mit Gemini auswerten"}
        </button>
      </div>
    </div>
  );
}

/* ── Vorschau fürs Umbenennen ───────────────────────────────────────────── */
function UmbenennenDialog({ onClose, onFertig }) {
  const [daten, setDaten] = useState(null);
  const [aus, setAus] = useState(() => new Set());
  const [dubAus, setDubAus] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");

  const laden = useCallback(async () => {
    try {
      const r = await api("/belege/namensvorschlaege?bereich=abrechnung");
      setDaten(r);
      setAus(new Set((r.vorschlaege || [])
        .filter((v) => v.neu && !v.unveraendert && !v.konflikt).map((v) => v.id)));
      setDubAus(new Set());
    } catch (e) { setFehler(String(e.message || e)); }
  }, []);

  React.useEffect(() => { laden(); }, [laden]);

  const umbenennen = async () => {
    setBusy(true); setFehler("");
    try {
      const r = await api("/belege/umbenennen", "POST", { ids: [...aus] });
      onFertig(`${r.umbenannt} Dateien umbenannt` + (r.fehler ? `, ${r.fehler} Fehler.` : "."));
    } catch (e) { setFehler(String(e.message || e)); setBusy(false); }
  };

  const dublettenWeg = async () => {
    setBusy(true); setFehler("");
    try {
      const r = await api("/belege/dubletten-entfernen", "POST", { ids: [...dubAus] });
      onFertig(`${r.entfernt} doppelte Dateien in den Dropbox-Papierkorb gelegt` +
               (r.fehler ? `, ${r.fehler} Fehler.` : "."));
    } catch (e) { setFehler(String(e.message || e)); setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.dialog, width: 900, maxHeight: "80vh", overflow: "auto" }}
           onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px" }}>Namen fürs Schema</h3>
        <p style={{ fontSize: "0.85rem", color: "var(--color-gray-600)", margin: "0 0 12px" }}>
          {daten ? `${daten.umbenennbar} von ${daten.anzahl} könnten umbenannt werden` : "Lade…"}
          {daten?.konflikte ? ` · ${daten.konflikte} Namenskonflikte (nicht vorausgewählt)` : ""}
          . Noch ist nichts passiert.
        </p>
        {fehler && <div style={S.err}>{fehler}</div>}

        {daten?.dubletten > 0 && (
          <div style={{ border: "1px solid var(--color-warning)", borderRadius: 8, padding: 12, marginBottom: 14 }}>
            <strong style={{ fontSize: "0.9rem" }}>
              {daten.dubletten} Dateien liegen doppelt herum
            </strong>
            <p style={{ fontSize: "0.8rem", color: "var(--color-gray-600)", margin: "4px 0 8px" }}>
              Byte-gleiche Abrechnungen — meist mehrfach heruntergeladen. Eine bleibt jeweils
              stehen, die übrigen können weg. Sie landen im Dropbox-Papierkorb und sind von dort
              wiederherstellbar.
            </p>
            {daten.dubletten_gruppen.map((g) => (
              <div key={g.behalten.id} style={{ fontSize: "0.78rem", marginBottom: 6 }}>
                <div style={{ color: "var(--color-gray-600)" }}>
                  {g.datum} · {eur(g.betrag)} — bleibt: <strong>{g.behalten.datei}</strong>
                </div>
                {g.loeschen.map((x) => (
                  <label key={x.id} style={{ display: "flex", gap: 6, paddingLeft: 12 }}>
                    <input type="checkbox" checked={dubAus.has(x.id)}
                           onChange={(e) => setDubAus((s) => {
                             const n = new Set(s);
                             if (e.target.checked) n.add(x.id); else n.delete(x.id);
                             return n;
                           })} />
                    <span style={{ wordBreak: "break-all" }}>{x.datei}</span>
                  </label>
                ))}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button style={S.btnMini}
                      onClick={() => setDubAus(new Set(daten.dubletten_gruppen
                        .flatMap((g) => g.loeschen.map((x) => x.id))))}>
                alle auswählen
              </button>
              <button style={S.btnRot} onClick={dublettenWeg} disabled={busy || dubAus.size === 0}>
                {busy ? "Entferne…" : `${dubAus.size} Doppel entfernen`}
              </button>
            </div>
          </div>
        )}
        {daten && (
          <table style={S.tabelle}>
            <thead><tr>
              <th style={S.th}></th><th style={S.th}>Bisher</th><th style={S.th}>Neu</th>
            </tr></thead>
            <tbody>
              {daten.vorschlaege.map((v) => (
                <tr key={v.id}>
                  <td style={S.td}>
                    <input type="checkbox" disabled={!v.neu || v.unveraendert}
                           checked={aus.has(v.id)}
                           onChange={(e) => setAus((s) => {
                             const n = new Set(s);
                             if (e.target.checked) n.add(v.id); else n.delete(v.id);
                             return n;
                           })} />
                  </td>
                  <td style={{ ...S.td, wordBreak: "break-all" }}>{v.alt}</td>
                  <td style={{ ...S.td, wordBreak: "break-all" }}>
                    {v.neu ? (v.unveraendert ? <span style={{ color: "var(--color-gray-500)" }}>{v.neu} (schon so)</span> : v.neu)
                           : <span style={{ color: "var(--color-gray-500)" }}>{v.grund}</span>}
                    {v.konflikt && <span style={S.pillWarn}>Name doppelt</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button style={S.btn} onClick={onClose} disabled={busy}>Abbrechen</button>
          <button style={S.btnPrim} onClick={umbenennen} disabled={busy || aus.size === 0}>
            {busy ? "Benenne um…" : `${aus.size} Dateien umbenennen`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Die Sektion selbst ─────────────────────────────────────────────────── */
export default function AbrechnungSektion({ daten, alleBelege, onGeaendert, onMeldung, aktiv, markiert, onWaehlen,
                                           aufklappen = false }) {
  const [offen, setOffen] = useState(false);
  // Welle 104: Suche/Filter oben mit Treffern hier → aufklappen
  useEffect(() => { if (aufklappen) setOffen(true); }, [aufklappen]);
  const [offeneId, setOffeneId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [umbenennen, setUmbenennen] = useState(false);

  const alleAuswerten = async () => {
    setBusy(true);
    try {
      const r = await api("/abrechnungen/auswerten-offene?limit=5", "POST");
      onMeldung(`${r.verarbeitet} Abrechnungen ausgewertet` +
                (r.fehler ? `, ${r.fehler} Fehler` : "") +
                (r.offen_danach ? ` — noch ${r.offen_danach} offen.` : "."));
      onGeaendert();
    } catch (e) { onMeldung("Fehler: " + e.message); }
    setBusy(false);
  };

  const belege = (daten.buendel || []).flatMap((b) => b.belege || []);

  return (
    <div style={S.karte}>
      <div style={S.kopf} onClick={() => setOffen((o) => !o)}>
        <span style={{ fontSize: "1rem" }}>{offen ? "▾" : "▸"}</span>
        <h2 style={S.kopfTitel}>{daten.name}</h2>
        <span style={S.zaehler}>
          {belege.length !== daten.anzahl ? `${belege.length} von ` : ""}
          {daten.anzahl} {daten.anzahl === 1 ? "Abrechnung" : "Abrechnungen"} der Versicherung
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <button style={S.btn} onClick={() => setUmbenennen(true)}>Namen vorschlagen</button>
          <button style={S.btn} onClick={alleAuswerten} disabled={busy}>
            {busy ? "Werte aus…" : "Offene auswerten (5)"}
          </button>
        </span>
      </div>

      {offen && (
        <table style={S.tabelle}>
          <thead><tr>
            <th style={S.th}>Datum</th><th style={S.th}>Vorgangsnummer</th>
            <th style={{ ...S.th, textAlign: "right" }}>Überwiesen</th>
            <th style={S.th}>Positionen</th><th style={S.th}>Datei</th><th style={S.th}></th>
          </tr></thead>
          <tbody>
            {belege.map((a) => (
              <React.Fragment key={a.id}>
                <tr data-krv-zeile={`abrechnung|${a.id}|`}
                    style={{ cursor: "pointer",
                             ...(markiert.has(`abrechnung|${a.id}|`) ? S.zeileMarkiert : null),
                             ...(aktiv === `abrechnung|${a.id}|` ? S.zeileGewaehlt : null) }}
                    onClick={(e) => onWaehlen(`abrechnung|${a.id}|`, e)}>
                  <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                    <span style={S.aufklapper} title="Positionen auf-/zuklappen"
                          onClick={(e) => { e.stopPropagation(); setOffeneId((x) => (x === a.id ? null : a.id)); }}>
                      {offeneId === a.id ? "▾" : "▸"}
                    </span>
                    {datum(a.datum_beleg)}
                  </td>
                  <td style={S.td}>{a.vorgangsnummer || "—"}</td>
                  <td style={{ ...S.td, ...S.num }}>{eur(a.betrag)}</td>
                  <td style={S.td}>
                    {a.positionen_anzahl
                      ? `${a.positionen_anzahl} Positionen, ${a.positionen_fertig || 0} zugeordnet`
                      : (
                        <span style={{ color: "var(--color-gray-500)" }}>
                          {a.positionen_am ? "ausgewertet, keine Positionen" : "noch nicht ausgewertet"}
                        </span>
                      )}
                  </td>
                  <td style={{ ...S.td, wordBreak: "break-all" }}>{a.datei_name}</td>
                  <td style={{ ...S.td, ...S.iconZelle }} onClick={(e) => e.stopPropagation()}>
                    <IconKnopf farbe="blau" titel="PDF rechts anzeigen" onClick={() => onWaehlen(`abrechnung|${a.id}|`, {})}>
                      <FaFilePdf />
                    </IconKnopf>
                  </td>
                </tr>
                {offeneId === a.id && (
                  <tr><td colSpan={6} style={{ padding: 0 }}>
                    <Positionen abrechnung={a} alleBelege={alleBelege} onGeaendert={onGeaendert} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {umbenennen && (
        <UmbenennenDialog onClose={() => setUmbenennen(false)}
                          onFertig={(m) => { setUmbenennen(false); onMeldung(m); onGeaendert(); }} />
      )}
    </div>
  );
}
